import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Building2, Users, Receipt, LogOut, Menu, X, Calendar, ChevronDown, ClipboardList, Wallet, Database, Settings, Map, User as UserIcon, TrendingUp, Boxes } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { api } from '@/App';
import { toast } from 'sonner';
import GlobalBalanceBanner from '@/components/GlobalBalanceBanner';
import { FontSizeSlider } from '@/components/FontSizeSlider';
import { isRouteAllowed, getAllowedRoutes, getDefaultRoute, isMaster } from '@/lib/permissions';

const Layout = ({ user, onLogout, companyName = 'Walet ERP' }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Route guard: redirect restricted roles away from disallowed URLs
  useEffect(() => {
    if (user && !isRouteAllowed(user.role, location.pathname)) {
      navigate(getDefaultRoute(user.role), { replace: true });
    }
  }, [location.pathname, user, navigate]);

  const [userPopoverOpen, setUserPopoverOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [loadingPeriods, setLoadingPeriods] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [currentCompanyName, setCurrentCompanyName] = useState(companyName);
  const [companyLogo, setCompanyLogo] = useState(null);

  const navItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/transactions', icon: Receipt, label: 'Transaksi' },
    { path: '/hutang-piutang', icon: Wallet, label: 'Hutang Piutang' },
    { path: '/investors', icon: Users, label: 'Investor' },
    { path: '/mitra', icon: Users, label: 'Mitra' },
    { path: '/branches', icon: Building2, label: 'Cabang' },
    { path: '/investments', icon: TrendingUp, label: 'Investasi' },
  ];

  const settingsSubMenu = [
    { path: '/pengaturan/general', label: 'General' },
    { path: '/pengaturan/periode', label: 'Periode' },
    { path: '/pengaturan/user', label: 'User' },
    { path: '/activity-logs', label: 'Log Aktivitas' },
    ...(isMaster(user) ? [
      { path: '/database-management', label: 'Database Management' },
      { path: '/database-editor', label: 'Database Editor' }
    ] : []),
    { path: '/pengaturan/whatsapp', label: 'WhatsApp' }
  ];

  useEffect(() => {
    fetchPeriods();
    
    // Listen untuk periods update dari SettingsPeriod
    const handlePeriodsUpdate = () => {
      console.log('periodsUpdated event received, refreshing periods...');
      fetchPeriods();
    };
    
    // Listen for settings updates to refresh company name and logo
    const handleSettingsUpdate = async () => {
      try {
        const response = await api.get('/settings');
        const name = response.data.company_name || 'Walet ERP';
        const logo = response.data.company_logo || null;
        setCurrentCompanyName(name);
        setCompanyLogo(logo);
      } catch (error) {
        console.error('Failed to fetch company settings:', error);
      }
    };

    // Fetch logo on initial load
    handleSettingsUpdate();
    
    window.addEventListener('periodsUpdated', handlePeriodsUpdate);
    window.addEventListener('settingsUpdated', handleSettingsUpdate);
    
    // Cleanup
    return () => {
      window.removeEventListener('periodsUpdated', handlePeriodsUpdate);
      window.removeEventListener('settingsUpdated', handleSettingsUpdate);
    };
  }, []);

  // Update local company name when prop changes
  useEffect(() => {
    setCurrentCompanyName(companyName);
  }, [companyName]);

  useEffect(() => {
    // Auto-expand settings menu if on settings page
    if (location.pathname.startsWith('/pengaturan')) {
      setSettingsOpen(true);
    }
  }, [location.pathname]);

  const fetchPeriods = async () => {
    try {
      const response = await api.get('/periods');
      setPeriods(response.data);
      
      // Check if there's a saved period selection in localStorage
      const savedPeriodId = localStorage.getItem('selectedPeriodId');
      
      // If saved period exists and is valid, use it
      if (savedPeriodId && response.data.find(p => p.id === savedPeriodId)) {
        setSelectedPeriod(savedPeriodId);
      } else {
        // Otherwise, set active period as default
        const activePeriod = response.data.find(p => !p.is_closed);
        if (activePeriod) {
          setSelectedPeriod(activePeriod.id);
          localStorage.setItem('selectedPeriodId', activePeriod.id);
        } else if (response.data.length > 0) {
          // If no active period, select the most recent closed period
          setSelectedPeriod(response.data[0].id);
          localStorage.setItem('selectedPeriodId', response.data[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch periods:', error);
    } finally {
      setLoadingPeriods(false);
    }
  };

  const handlePeriodChange = (periodId) => {
    setSelectedPeriod(periodId);
    localStorage.setItem('selectedPeriodId', periodId);
    
    // Trigger a custom event so Dashboard can listen to it
    window.dispatchEvent(new CustomEvent('periodChanged', { detail: { periodId } }));
    
    const period = periods.find(p => p.id === periodId);
    toast.success(`Periode diubah ke: ${period?.name}`);
  };

  const getCurrentPeriod = () => {
    return periods.find(p => p.id === selectedPeriod);
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {companyLogo ? (
                  <img 
                    src={companyLogo} 
                    alt="Company Logo" 
                    className="w-12 h-12 object-contain rounded-lg"
                  />
                ) : null}
                <div>
                  <h1 className="text-2xl font-bold text-teal-600" style={{ fontFamily: 'Space Grotesk' }}>{currentCompanyName}</h1>
                  <button
                    onClick={onLogout}
                    className="mt-1 inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-medium text-red-600 transition-colors hover:bg-red-100"
                    data-testid="logout-btn"
                  >
                    <LogOut size={13} />
                    Logout
                  </button>
                </div>
              </div>
              <button
                className="lg:hidden text-slate-500 hover:text-slate-700"
                onClick={() => setSidebarOpen(false)}
                data-testid="close-sidebar-btn"
              >
                <X size={24} />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.filter((item) => isRouteAllowed(user?.role, item.path)).map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                    isActive(item.path)
                      ? 'bg-teal-50 text-teal-600 font-medium'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <Icon size={20} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
            
            {/* Asset (Rumah & Mobil managed via tabs inside the page) */}
            {isRouteAllowed(user?.role, '/assets') && (
            <Link
              to="/assets"
              onClick={() => setSidebarOpen(false)}
              data-testid="nav-asset"
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                location.pathname.startsWith('/assets')
                  ? 'bg-teal-50 text-teal-600 font-medium'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <Boxes size={20} />
              <span>Asset</span>
            </Link>
            )}

            {/* Peta */}
            {isRouteAllowed(user?.role, '/peta') && (
            <Link
              to="/peta"
              onClick={() => setSidebarOpen(false)}
              data-testid="nav-peta"
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                isActive('/peta')
                  ? 'bg-teal-50 text-teal-600 font-medium'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <Map size={20} />
              <span>Peta</span>
            </Link>
            )}
            
            {/* Settings Menu with Sub-menus (only for unrestricted roles) */}
            {getAllowedRoutes(user?.role) === null && (
            <div className="space-y-1">
              <button
                onClick={() => setSettingsOpen(!settingsOpen)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all ${
                  location.pathname.startsWith('/pengaturan')
                    ? 'bg-teal-50 text-teal-600 font-medium'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Settings size={20} />
                  <span>Pengaturan</span>
                </div>
                <ChevronDown
                  size={16}
                  className={`transform transition-transform ${settingsOpen ? 'rotate-180' : ''}`}
                />
              </button>
              
              {/* Sub-menu items */}
              {settingsOpen && (
                <div className="ml-8 space-y-1">
                  {settingsSubMenu.map((subItem) => (
                    <Link
                      key={subItem.path}
                      to={subItem.path}
                      onClick={() => setSidebarOpen(false)}
                      className={`block px-4 py-2 rounded-lg text-sm transition-all ${
                        location.pathname === subItem.path
                          ? 'bg-teal-50 text-teal-600 font-medium'
                          : 'text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      {subItem.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            )}

            {/* Pengatur ukuran font (seperti aA di Safari) */}
            <FontSizeSlider />
          </nav>

          {/* User Info & Period Selector */}
          <div className="p-4 border-t border-slate-200 space-y-3">
            <Popover open={userPopoverOpen} onOpenChange={setUserPopoverOpen}>
              <PopoverTrigger asChild>
                <div className="flex items-center gap-3 cursor-pointer hover:bg-slate-50 p-2 rounded-lg transition-colors">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center text-white font-semibold overflow-hidden border-2 border-white shadow-sm">
                    {user.username?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate" data-testid="user-name">{user.username}</p>
                    <p className="text-xs text-slate-500 truncate">{user.role.replace('_', ' ').toUpperCase()}</p>
                  </div>
                </div>
              </PopoverTrigger>
              <PopoverContent side="right" className="w-80">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center text-white font-bold text-2xl overflow-hidden border-3 border-teal-200 shadow-lg">
                      {user.username?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-lg text-slate-800">{user.username}</h3>
                      <p className="text-xs text-teal-600 font-medium mt-1">
                        {user.role.replace('_', ' ').toUpperCase()}
                      </p>
                    </div>
                  </div>
                  <div className="border-t pt-3">
                    <button
                      onClick={() => {
                        setUserPopoverOpen(false);
                        onLogout();
                      }}
                      className="flex w-full items-center justify-start gap-2 rounded-md border border-red-200 px-4 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
                      data-testid="logout-popover-btn"
                    >
                      <LogOut size={16} />
                      Logout
                    </button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            {/* Period Selector */}
            <div className="space-y-2">
              <label className="text-xs font-medium text-slate-600 flex items-center gap-1">
                <Calendar size={12} />
                Pilih Periode
              </label>
              {loadingPeriods ? (
                <div className="h-9 bg-slate-100 rounded animate-pulse"></div>
              ) : periods.length > 0 ? (
                <Select value={selectedPeriod} onValueChange={handlePeriodChange}>
                  <SelectTrigger className="w-full" data-testid="period-selector">
                    <SelectValue placeholder="Pilih periode" />
                  </SelectTrigger>
                  <SelectContent>
                    {periods.map((period) => (
                      <SelectItem key={period.id} value={period.id}>
                        <div className="flex items-center gap-2">
                          <span>{period.name}</span>
                          {!period.is_closed && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Aktif</span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-xs text-slate-500 italic">Belum ada periode</p>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile Header */}
        <header className="lg:hidden bg-white border-b border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {companyLogo && (
                <img 
                  src={companyLogo} 
                  alt="Company Logo" 
                  className="w-8 h-8 object-contain rounded"
                />
              )}
              <h1 className="text-xl font-bold text-teal-600" style={{ fontFamily: 'Space Grotesk' }}>{currentCompanyName}</h1>
            </div>
            <button
              onClick={() => setSidebarOpen(true)}
              className="text-slate-600 hover:text-slate-800"
              data-testid="open-sidebar-btn"
            >
              <Menu size={24} />
            </button>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto">
          <GlobalBalanceBanner />
          <div className="p-6">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Layout;
